
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { toast } from 'react-hot-toast';

interface Order {
    _id: string;
    orderNumber: string;
    items: Array<{
        name: string;
        price: number;
        quantity: number;
    }>;
    totalAmount: number;
    status: string;
    createdAt: string;
}

export const BillingPage: React.FC = () => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            const res = await api.get('/payment/orders');
            setOrders(res.data);
        } catch (error) {
            toast.error('Impossible de charger l\'historique');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-navy-950 text-cream-100 p-8 font-serif">
            <header className="mb-12">
                <h1 className="text-3xl font-bold text-gold-500 mb-2">Historique de Commandes</h1>
                <p className="text-navy-300">Retrouvez ici toutes vos acquisitions de souvenirs.</p>
            </header>

            {loading ? (
                <div className="flex justify-center h-64">
                    <div className="w-12 h-12 border-4 border-gold-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
            ) : orders.length === 0 ? (
                <div className="text-center py-20 bg-navy-900/50 rounded-xl border border-navy-800">
                    <p className="text-navy-400 text-lg">Aucune commande pour le moment.</p>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-navy-700 shadow-xl">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-navy-900 border-b border-navy-700 text-gold-400 uppercase text-sm tracking-wider">
                                <th className="p-4">Référence</th>
                                <th className="p-4">Date</th>
                                <th className="p-4">Contenu</th>
                                <th className="p-4">Montant</th>
                                <th className="p-4">Statut</th>
                                <th className="p-4 text-right">Facture</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-navy-800 bg-navy-900/40">
                            {orders.map((order, index) => (
                                <motion.tr
                                    key={order._id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="hover:bg-navy-800/50 transition-colors"
                                >
                                    <td className="p-4 font-mono text-sm text-navy-300">{order.orderNumber}</td>
                                    <td className="p-4 text-sm">
                                        {new Date(order.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="p-4">
                                        {order.items.map((item, i) => (
                                            <div key={i} className="text-cream-100 font-medium">
                                                {item.quantity}x {item.name}
                                            </div>
                                        ))}
                                    </td>
                                    <td className="p-4 font-bold text-gold-400">
                                        {(order.totalAmount / 100).toFixed(2)} €
                                    </td>
                                    <td className="p-4">
                                        <span className={`px-2 py-1 rounded text-xs font-bold ${order.status === 'PAID' ? 'bg-emerald-500/20 text-emerald-400' :
                                                order.status === 'PENDING' ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'
                                            }`}>
                                            {order.status === 'PAID' ? 'PAYÉ' : order.status}
                                        </span>
                                    </td>
                                    <td className="p-4 text-right">
                                        <button disabled className="text-navy-500 cursor-not-allowed text-sm hover:text-navy-400" title="Bientôt disponible">
                                            📥 Télécharger
                                        </button>
                                    </td>
                                </motion.tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
